import React, { useState } from 'react'
import Select from "react-select"
import Creatable from "react-select/creatable"
import pluralize from 'pluralize'
import { Upload, Switch, message, Drawer } from 'antd'

import { useListData, usePet, useToggle } from '../../modules'

import Receive from '../reusable/receive'
import SelectLabel from '../reusable/selectLabel'

import Ingredients from '../modals/ingredients'
import Variant from '../modals/variant'

const preauthCategoryOptions = [
    { label: 'Surgical', value: 'SURGICAL' },
    { label: 'Imaging', value: 'IMAGING' },
    { label: 'Renal', value: 'RENAL' },
    { label: 'Oncology', value: 'ONCOLOGY' },
    { label: 'Optical', value: 'OPTICAL' },
]

const NewSaleItem = ({ toggle, visible, onPerform, type = 'Sale Item' }) => {
    const [name, setName] = useState(null)
    const [category, setCategory] = useState(null)
    const [invRef, setInvRef] = useState(null)
    const [conversionRate, setConversionRate] = useState('1')

    const [price, setPrice] = useState(null)
    const [variants, setVariants] = useState([])
    const [categories, setCategories] = useState([])
    const [itemGroups, setItemGroups] = useState([])
    const [routingGroups, setRoutingGroups] = useState([])
    const [accounts, setAccounts] = useState([])
    const [account, setAccount] = useState({})
    const [form, setForm] = useState()
    const [itemGroup, setItemGroup] = useState(null)
    const [routingGroup, setRoutingGroup] = useState(null)
    const [preauthRequired, setPreauthRequired] = useState(false)
    const [preauthCategory, setPreauthCategory] = useState(null)

    const [ingredients, setIngredients] = useState([])
    const [imageUrl, setImageUrl] = useState(null)
    const [image, setImage] = useState(null)
    const [string, setString] = useState('')
    const [maxDiscount, setMaxDsc] = useState(0)
    
    const [hasVariants, toggleHasVariants] = useToggle()
    const [imgLoading, setImgLoading] = useState(false)
    const [ingredientsModal, toggleIngredientsModal] = useToggle()
    const [variantsModal, toggleVariantsModal] = useToggle()

    const [{ loading: invLoading, fetch: fetchInvItems }, { list: items = [] }] = useListData('inventoryItems', { string }, { notify: true }, 'items')
    const [{ loading: formsLoading, fetch: fetchForms }, { list: forms = [] }] = useListData('inputForms', { string }, { notify: true }, 'forms', 100)
    
    const [, 
        { navigate, Paginate, page = 1, limit = 10 }
    ] = useListData('accounts', { string, types: ['Expenses', 'Cost of Goods Sold'] }, { notify: true, manual: false }, 'accounts', 100, ({ accounts: data }) => {
        let obj = {}
        
        data.forEach(acc => {
            if (!obj[acc.type])
                obj[acc.type] = []

            obj[acc.type].push({
                label: acc.name,
                value: acc._id
            })
        })

        let final = []
        for (let typ in obj) {
            final.push({
                label: typ,
                options: obj[typ]
            })
        }

        setAccounts(final)
    })
    
    const search = str => {
        if (str?.trim()?.length > 2) {
            setString(str)
            fetchInvItems()
        }
    }

    const onComplete = ({}, notif, ops) => {
        onPerform()
        notif('Sale item has been added', ops('success'))
        
        setTimeout(toggle, 300)
    }

    const [{ loading }, execute] = usePet(hasVariants ? 'newSaleItemWithVariants' : 'newSaleItem', {
        name,
        type,
        ref: invRef ? invRef._id : null,
        category: category?.value,
        routingGroup: routingGroup?.value ?? null,
        itemGroup: itemGroup?.value ?? null,
        ingredients: ingredients?.map(itm => ({
            item: itm._id,
            quantity: itm.qty
        })),
        form: form?.value,
        account: account?.value,
        variants: variants.map(({ name: nm, conversionRate, price: prc, ref, ingredients: ing, maxDiscount: mxDsc, form }) => ({
            name: nm,
            price: prc,
            conversionRate,
            maxDiscount: mxDsc,
            form: form?.value,
            ref: ref ? ref._id : null,
            account: account?.value,
            ingredients: ing?.map(itm => ({
                item: itm._id,
                quantity: itm.qty
            }))
        })),
        price,
        image,
        conversionRate,
        maxDiscount,
        preauthRequired,
        preauthCategory: preauthRequired ? preauthCategory?.value : null,
    }, { notify: true, hasFile: true, callback: onComplete, manual: true, load: { msg: 'Adding new item...' } })

    const [{ loading: catsLoading, error: catsError }] = usePet('saleItemCategories', null, { notify: true, callback: ({ data }) => setCategories(data) })
    usePet('itemGroups', null, {
        notify: true, callback: ({ data }) => {
            data = (data || []).filter(grp => grp.name !== 'Bill').map(grp => ({ label: grp.name, value: grp._id }))
            setItemGroups(data)
        }
    })
    usePet('routingGroups', null, {
        notify: true, callback: ({ data }) => {
            data = (data || []).map(grp => ({ label: grp.name, value: grp._id }))
            setRoutingGroups(data)
        }
    })

    const modify = e => {
        switch (e.target.name) {
            case 'name':
                setName(e.target.value)
                break

            case 'price':
                setPrice(e.target.value)
                break

            case 'max-discount':
                setMaxDsc(e.target.value)
                break

            case 'conversion-rate':
                setConversionRate(e.target.value)
                break

            default:
                break
        }
    }

    const getBase64 = (img, callback) => {
        const reader = new FileReader()
        reader.addEventListener('load', () => callback(reader.result))
        reader.readAsDataURL(img)
    }

    const beforeUpload = file => {
        const isImage = file.type === 'image/jpeg' || file.type === 'image/png'
        if (!isImage) {
            message.warning('Please select an image file')
            return false
        }

        const sizeOK = file.size / 1024 / 1024 <= 1
        if (!sizeOK) {
            message.warning('Please select an image smaller than 1MB')
            return false
        }

        setImageUrl(null)
        setImage(file)
        setImgLoading(true)

        setTimeout(() => {
            setImageUrl(URL.createObjectURL(file))
            setImgLoading(false)
        }, 1000)

        return false
    }

    const handleChange = info => {
        if (info.file.status === 'uploading') {
            setImgLoading(true)
            return
        }

        if (info.file.status === 'done') {
            getBase64(info.file.originFileObj, url => {
                setImageUrl(url)
                setImgLoading(false)
            })
        }
    }

    const removeImage = () => {
        setImage(null)
        setImageUrl(null)
    }

    const deleteIngredient = i => {
        let temp = [...ingredients]
        temp = temp.filter((_, index) => i !== index)

        setIngredients(temp)
    }

    const addVariant = vrnt => {
        let temp = [...variants]
        temp.push(vrnt)

        setVariants(temp)
    }

    const deleteVariant = i => {
        let temp = [...variants]
        temp = temp.filter((_, index) => i !== index)

        setVariants(temp)
    }

    return (
        <Drawer
            closable
            maskClosable
            visible={visible}
            onClose={toggle}
            footer={
                <div className="w-full text-right">
                    <button disabled={ loading } onClick={ execute } type="submit" className="text-white hisani-btn-green-light font-medium rounded-lg text-sm px-5 py-2.5 text-center">Save</button>
                </div>
            }
            width="30%">
            <p className="font-extrabold text-3xl mb-0">New {type}</p>
            <div className="divide-y divide-light-gray-400">
                <Variant
                    setString={ setString }
                    loading={ invLoading }
                    fetch={ fetchInvItems }
                    items={ items }
                    type={ type }
                    forms={ forms }
                    onSave={ addVariant}
                    visible={variantsModal}
                    toggle={toggleVariantsModal} />
                <Ingredients
                    ingredients={ ingredients }
                    onSave={setIngredients}
                    visible={ingredientsModal}
                    toggle={toggleIngredientsModal} />
                {/* <div className="w-full flex flex-col md:flex-row"> */}
                    {/* <div className="w-full md:w-3/12 h-auto text-center m-auto lg:block lg:w-5/12">
                        <p className="text-gray-500 text-xs italic">Image of Item</p>
                        <Upload
                            name="avatar"
                            listType="picture-card"
                            className="avatar-uploader"
                            showUploadList={false}
                            action="https://www.mocky.io/v2/5cc8019d300000980a055e76"
                            beforeUpload={beforeUpload}
                            onChange={handleChange}>
                            {
                                imageUrl ?
                                    <img
                                        src={imageUrl}
                                        alt="avatar"
                                        style={{ width: '100%' }} />
                                    :
                                    <div>
                                        {imgLoading ? <LoadingOutlined /> : <PlusOutlined />}
                                        <div style={{ marginTop: 8 }}>{imgLoading ? 'Generating preview...' : 'Upload image'}</div>
                                    </div>
                            }
                        </Upload>
                        {
                            image && <p onClick={removeImage} className="text-red-500 cursor-pointer text-xs italic">X Remove</p>
                        }
                    </div> */}
                    {/* <div className="w-full lg:w-7/12 bg-white p-5"> */}
                        <div className="flex flex-wrap -mx-3 pt-5 mb-3">
                            <div className="w-full md:w-1/2 px-3 mb-6 md:mb-0">
                                <label className="block uppercase tracking-wide text-gray-700 text-xs font-bold mb-2" htmlFor="grid-name">
                                    Name
                                </label>
                                <input
                                    className="appearance-none block w-full bg-gray-200 text-gray-700 border border-gray-200 rounded py-3 px-4 mb-3 leading-tight focus:outline-none focus:bg-white"
                                    id="grid-name"
                                    onChange={modify}
                                    value={name}
                                    type="text"
                                    name="name"
                                    placeholder="Item A" />
                            </div>
                            <div className="w-full md:w-1/2 px-3 mb-6 md:mb-0">
                                <label className="block uppercase tracking-wide text-gray-700 text-xs font-bold mb-2" htmlFor="grid-name">
                                    Category
                                </label>
                                <p className="text-gray-500 text-xs italic mb-2">A display label only (e.g. "Doctor", "Lab"). Does not route orders or control ordering — use Department Queue and Billing & Inventory Group below.</p>
                                <Receive
                                    loading={catsLoading}
                                    error={catsError}>
                                    <Creatable
                                        value={category}
                                        onCreateOption={ val => {
                                            let temp = [...categories]
                                            temp.push(val)
                                            setCategories(temp)
                                            setCategory({ label: val, value: val })
                                        }}
                                        onChange={setCategory}
                                        options={categories.map(cat => ({ label: cat, value: cat }))}
                                        placeholder="Select Category" />
                                </Receive>
                            </div>
                        </div>
                        <div className="flex flex-wrap -mx-3 pt-4 mb-3">
                            <div className="w-full md:w-1/2 px-3 mb-6 md:mb-0">
                                <label className="block uppercase tracking-wide text-gray-700 text-xs font-bold mb-2" htmlFor="grid-name">
                                    Variants
                                </label>
                                <div>
                                    <Switch
                                        onChange={ () => {
                                            toggleHasVariants()
                                            setIngredients([])
                                            setPrice(null)
                                        }}
                                        checked={hasVariants} />
                                    <p className="text-gray-500 text-xs italic">Has different types</p>
                                </div>
                            </div>
                            {
                                type === 'Sale Item' && !hasVariants &&
                                <div className="w-full md:w-1/2 px-3 mb-6 md:mb-0">
                                    <label className="block uppercase tracking-wide text-gray-700 text-xs font-bold mb-2" htmlFor="grid-name">
                                        Conversion Rate
                                    </label>
                                    <input
                                        className="appearance-none block w-full bg-gray-200 text-gray-700 border border-gray-200 rounded py-3 px-4 mb-3 leading-tight focus:outline-none focus:bg-white"
                                        id="grid-conversion-rate"
                                        type="text"
                                        name="conversion-rate"
                                        value={conversionRate}
                                        onChange={modify}
                                        placeholder="1/2" />
                                </div>
                            }
                        </div>
                        <div className="flex flex-wrap -mx-3 pt-4 mb-3">
                            <div className="w-full md:w-1/2 px-3 mb-6 md:mb-0">
                                <label className="block uppercase tracking-wide text-gray-700 text-xs font-bold mb-2" htmlFor="grid-username">
                                    Income Account
                                </label>
                                <Select
                                    className="mb-3"
                                    value={account}
                                    onChange={setAccount}
                                    formatGroupLabel={ SelectLabel }
                                    options={accounts}
                                    placeholder="Select account" />
                            </div>
                            {
                                type === 'Service' && !hasVariants &&
                                <div className="w-full md:w-1/2 px-3 mb-6 md:mb-0">
                                    <label className="block uppercase tracking-wide text-gray-700 text-xs font-bold mb-2" htmlFor="grid-username">
                                        Input Form
                                    </label>
                                    <Select
                                        value={ form }
                                        className="mb-3"
                                        options={ forms.map(frm => ({ value: frm._id, label: frm.name })) }
                                        onChange={ setForm }
                                        isLoading={ formsLoading }
                                        placeholder="Select Input Form" />
                                </div>
                            }
                        </div>
                        <div className="flex flex-wrap -mx-3 pt-2 mb-3">
                            <div className="w-full md:w-1/2 px-3 mb-6 md:mb-0">
                                <label className="block uppercase tracking-wide text-gray-700 text-xs font-bold mb-2">
                                    Department Queue
                                </label>
                                <p className="text-gray-500 text-xs italic mb-2">Controls which department queue this item routes to after billing.</p>
                                <Select
                                    isClearable
                                    value={routingGroup}
                                    onChange={setRoutingGroup}
                                    options={routingGroups}
                                    placeholder="Select department queue" />
                            </div>
                            <div className="w-full md:w-1/2 px-3 mb-6 md:mb-0">
                                <label className="block uppercase tracking-wide text-gray-700 text-xs font-bold mb-2">
                                    Billing & Inventory Group
                                </label>
                                <p className="text-gray-500 text-xs italic mb-2">Controls where clinicians can order this item and which default store can be used.</p>
                                <Select
                                    isClearable
                                    value={itemGroup}
                                    onChange={setItemGroup}
                                    options={itemGroups}
                                    placeholder="Select billing & inventory group" />
                            </div>
                        </div>
                        {
                            type === 'Service' && !hasVariants &&
                            <>
                                <div className="flex flex-wrap -mx-3 pt-4 mb-3">
                                    <div className="w-full md:w-1/2 px-3 mb-6 md:mb-0">
                                        <label className="block uppercase tracking-wide text-gray-700 text-xs font-bold mb-2">
                                            Requires Preauth
                                        </label>
                                        <div>
                                            <Switch
                                                checked={preauthRequired}
                                                onChange={checked => {
                                                    setPreauthRequired(checked)
                                                    if (!checked) setPreauthCategory(null)
                                                }} />
                                            <p className="text-gray-500 text-xs italic">Enable when this service needs insurer preauthorization before billing.</p>
                                        </div>
                                    </div>
                                    {
                                        preauthRequired &&
                                        <div className="w-full md:w-1/2 px-3 mb-6 md:mb-0">
                                            <label className="block uppercase tracking-wide text-gray-700 text-xs font-bold mb-2">
                                                Preauth Category
                                            </label>
                                            <Select
                                                value={preauthCategory}
                                                onChange={setPreauthCategory}
                                                options={preauthCategoryOptions}
                                                placeholder="Select category" />
                                        </div>
                                    }
                                </div>
                            </>
                        }
                        {
                            hasVariants &&
                            <div className="font-bold mt-3">
                                <div className="flex justify-between items-center">
                                    <p className="font-bold my-3">Variants</p>
                                    <button onClick={toggleVariantsModal} className="btn hisani-btn-green-light text-white my-3">
                                        <svg className="w-4 h-4 fill-current opacity-50 flex-shrink-0" viewBox="0 0 16 16">
                                            <path d="M15 7H9V1c0-.6-.4-1-1-1S7 .4 7 1v6H1c-.6 0-1 .4-1 1s.4 1 1 1h6v6c0 .6.4 1 1 1s1-.4 1-1V9h6c.6 0 1-.4 1-1s-.4-1-1-1z" />
                                        </svg>
                                        <span className="hidden xs:block ml-2">Add</span>
                                    </button>
                                </div>
                                {
                                    variants.length > 0 ?
                                        <table className="table-auto w-full mb-3">
                                            {/* Table header */}
                                            <thead className="text-xs uppercase text-gray-400 bg-gray-50 rounded-sm">
                                                <tr>
                                                    <th className="p-2">
                                                        <div className="font-semibold text-left">No</div>
                                                    </th>
                                                    <th className="p-2">
                                                        <div className="font-semibold text-left">Variant</div>
                                                    </th>
                                                    <th className="p-2">
                                                        <div className="font-semibold text-left">C / Rate</div>
                                                    </th>
                                                    <th className="p-2">
                                                        <div className="font-semibold text-left">Price</div>
                                                    </th>
                                                    <th />
                                                </tr>
                                            </thead>
                                            {/* Table body */}
                                            <tbody className="text-sm font-medium divide-y divide-gray-100">
                                                {
                                                    variants.map((variant, key) =>
                                                        <tr>
                                                            <td className="p-2 text-left">{key + 1}</td>
                                                            <td className="p-2 text-left">{variant.name}</td>
                                                            <td className="p-2 text-left">{variant.conversionRate}</td>
                                                            <td className="p-2 text-left">Ksh. {variant.price}</td>
                                                            <td onClick={() => deleteVariant(key)} className="p-2 text-left text-red-500 cursor-pointer">X</td>
                                                        </tr>
                                                    )
                                                }
                                            </tbody>
                                        </table>
                                        :
                                        <div className="text-center my-4">
                                            <h3>Please click above to add variants</h3>
                                        </div>
                                }
                            </div>
                        }
                        <div className="flex flex-wrap -mx-3 pt-4 mb-3">
                            {
                                !hasVariants &&
                                <>
                                    <div className="w-full md:w-1/2 px-3 mb-6 md:mb-0">
                                        <label className="block uppercase tracking-wide text-gray-700 text-xs font-bold mb-2" htmlFor="grid-name">
                                            Price
                                        </label>
                                        <input
                                            className="appearance-none block w-full bg-gray-200 text-gray-700 border border-gray-200 rounded py-3 px-4 mb-3 leading-tight focus:outline-none focus:bg-white"
                                            id="grid-price"
                                            value={price}
                                            onChange={modify}
                                            name="price"
                                            type="number"
                                            placeholder="XXXX" />
                                    </div>
                                    <div className="w-full md:w-1/2 px-3 mb-6 md:mb-0">
                                        <label className="block uppercase tracking-wide text-gray-700 text-xs font-bold mb-2" htmlFor="grid-name">
                                            Max Discount
                                        </label>
                                        <input
                                            className="appearance-none block w-full bg-gray-200 text-gray-700 border border-gray-200 rounded py-3 px-4 mb-3 leading-tight focus:outline-none focus:bg-white"
                                            id="grid-max-discount"
                                            value={ maxDiscount }
                                            onChange={modify}
                                            name="max-discount"
                                            type="number"
                                            placeholder="XXXX" />
                                    </div>
                                </>
                            }
                        </div>
                        {
                            !hasVariants ?
                                type === 'Prepared Item' ?
                                    <div className="font-bold mt-3">
                                        <div className="flex justify-between items-center">
                                            <p className="font-bold my-3">Ingredients</p>
                                            <button onClick={toggleIngredientsModal} className="btn hisani-btn-green-light text-white my-3">
                                                <svg className="w-4 h-4 fill-current opacity-50 flex-shrink-0" viewBox="0 0 16 16">
                                                    <path d="M15 7H9V1c0-.6-.4-1-1-1S7 .4 7 1v6H1c-.6 0-1 .4-1 1s.4 1 1 1h6v6c0 .6.4 1 1 1s1-.4 1-1V9h6c.6 0 1-.4 1-1s-.4-1-1-1z" />
                                                </svg>
                                                <span className="hidden xs:block ml-2">Add</span>
                                            </button>
                                        </div>
                                        {
                                            ingredients.length > 0 ?
                                                <table className="table-auto w-full mb-3">
                                                    {/* Table header */}
                                                    <thead className="text-xs uppercase text-gray-400 bg-gray-50 rounded-sm">
                                                        <tr>
                                                            <th className="p-2">
                                                                <div className="font-semibold text-left">No</div>
                                                            </th>
                                                            <th className="p-2">
                                                                <div className="font-semibold text-left">Selected</div>
                                                            </th>
                                                            <th className="p-2">
                                                                <div className="font-semibold text-left">Quantity</div>
                                                            </th>
                                                            <th />
                                                        </tr>
                                                    </thead>
                                                    {/* Table body */}
                                                    <tbody className="text-sm font-medium divide-y divide-gray-100">
                                                        {
                                                            ingredients.map((ingredient, key) =>
                                                                <tr>
                                                                    <td className="p-2 text-left">{key + 1}</td>
                                                                    <td className="p-2 text-left">{ingredient.name}</td>
                                                                    <td className="p-2 text-left">{ingredient.qty} {pluralize(ingredient.issuedIn, ingredient.qty)}</td>
                                                                    <td onClick={ () => deleteIngredient(key) } className="p-2 text-left text-red-500 cursor-pointer">X</td>
                                                                </tr>
                                                            )
                                                        }
                                                    </tbody>
                                                </table>
                                                :
                                                <div className="text-center my-4">
                                                    <h3>Please click above to add ingredients</h3>
                                                </div>
                                        }
                                    </div>
                                    : type === 'Sale Item' ?
                                        <div className="flex flex-wrap -mx-3 pt-4 mb-6 bt-1 border-gray-200">
                                            <div className="w-full md:w-2/3 px-3 mb-6 md:mb-0">
                                                <label className="block uppercase tracking-wide text-gray-700 text-xs font-bold mb-2" htmlFor="grid-name">
                                                    Inventory Item
                                                </label>
                                                <Select
                                                    isLoading={invLoading}
                                                    loadingMessage={string => `Searching for "${string?.inputValue}"`}
                                                    onInputChange={search}
                                                    value={invRef}
                                                    onChange={setInvRef}
                                                    options={items.map(item => ({ label: item.name, value: item._id, ...item }))}
                                                    placeholder="Search for inventory item" />
                                                <p className="text-gray-500 text-xs italic">Which inventory item does it refer to?</p>
                                            </div>
                                        </div>
                                        : null
                                : null
                        }
                    {/* </div>
                </div> */}

            </div>
        </Drawer>
    )
}

export default NewSaleItem
